import attractionData from '@App/components/Map/data/attraction-data';
import postalLocalities from '../Introduction/data/belgian-postal-localities.json';

const DEFAULT_RESIDENCE = [4.35, 50.85];

const municipalities = new Map();
const postcodeByMunicipality = postalLocalities.reduce((items, locality) => {
  const postcode = locality.postcodes?.[0];

  if (!locality.name || !postcode || items.has(locality.name)) {
    return items;
  }

  items.set(locality.name, postcode);
  return items;
}, new Map());

attractionData.features.forEach((feature) => {
  const name = feature.properties?.municipality;
  const coordinates = feature.geometry?.coordinates;

  if (!name || !coordinates) return;
  const current = municipalities.get(name) || {
    coordinates: [0, 0], count: 0, label: name, sublabel: null, value: name,
  };
  municipalities.set(name, {
    ...current,
    coordinates: [
      current.coordinates[0] + coordinates[0],
      current.coordinates[1] + coordinates[1],
    ],
    count: current.count + 1,
    sublabel: current.sublabel || postcodeByMunicipality.get(name) || null,
  });
});

const municipalityOptions = [...municipalities.values()]
  .map(municipality => ({
    ...municipality,
    coordinates: municipality.coordinates.map(value => value / municipality.count),
  }))
  .sort((first, second) => first.label.localeCompare(second.label));

function normalizeResidence(value) {
  return value
    ?.normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLocaleLowerCase('nl-BE');
}

function getResidenceName(residence) {
  return residence?.replace(/\s+\([^)]*\)$/, '');
}

function findMunicipality(residence) {
  const normalizedResidence = normalizeResidence(getResidenceName(residence));

  return municipalityOptions.find(option => normalizedResidence
    && normalizeResidence(option.label) === normalizedResidence);
}

function findPostalLocality(residence) {
  const name = normalizeResidence(getResidenceName(residence));
  const province = residence?.match(/\(([^)]*)\)$/)?.[1];

  return postalLocalities.find(locality => (
    normalizeResidence(locality.name) === name
    && (!province || normalizeResidence(locality.province) === normalizeResidence(province))
  ));
}

function getResidenceCoordinates(residence) {
  return findMunicipality(residence)?.coordinates || DEFAULT_RESIDENCE;
}

async function resolveResidenceCoordinates(residence) {
  const municipality = findMunicipality(residence);
  if (municipality) return municipality.coordinates;

  const postcode = findPostalLocality(residence)?.postcodes?.[0];
  if (!postcode) return DEFAULT_RESIDENCE;

  try {
    const response = await fetch(`https://api.zippopotam.us/be/${postcode}`);
    if (!response.ok) return DEFAULT_RESIDENCE;

    const data = await response.json();
    const place = data.places?.[0];
    const coordinates = [Number(place?.longitude), Number(place?.latitude)];

    return coordinates.every(Number.isFinite) ? coordinates : DEFAULT_RESIDENCE;
  } catch (error) {
    return DEFAULT_RESIDENCE;
  }
}

export {
  getResidenceCoordinates,
  municipalityOptions,
  resolveResidenceCoordinates,
};
